import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, DatePicker, Empty, Progress, Row, Segmented, Space, Spin, Statistic, Table, Typography } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import { formatMoney } from '../../../shared/utils/money';
import { fmtBytes, fmtCount, fmtGb, s3, toastErr } from './common';

const { Text } = Typography;

const FreeBar = ({ label, used, free, fmt }) => {
    const u = Number(used) || 0;
    const f = Number(free) || 0;
    const pct = f > 0 ? Math.min(100, Math.round((u / f) * 100)) : 100;
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Text>{label}</Text>
                <Text type="secondary">{fmt(u)} / {fmt(f)}</Text>
            </div>
            <Progress percent={pct} size="small" showInfo={false} status={f > 0 && u > f ? 'exception' : 'normal'} style={{ margin: 0 }} />
        </div>
    );
};

const S3Usage = () => {
    const { t } = useTranslation();
    const [month, setMonth] = useState(dayjs());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metric, setMetric] = useState('storage');

    const ym = month.format('YYYY-MM');
    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData((await s3.get('/usage', { month: ym })) || {});
        } catch (e) { toastErr(e, t('Failed to load')); } finally { setLoading(false); }
    }, [ym, t]);

    useEffect(() => { load(); }, [load]);

    const totals = data?.totals || {};
    const prices = data?.prices || {};
    const lines = Array.isArray(data?.lines) ? data.lines : [];
    const buckets = Array.isArray(data?.buckets) ? data.buckets : [];
    const daily = useMemo(() => (Array.isArray(data?.daily) ? data.daily : []), [data]);
    const invoices = Array.isArray(data?.invoices) ? data.invoices : [];
    const usd = (v) => formatMoney(v ?? 0, prices.currency || 'USD');

    const chart = useMemo(() => {
        const M = {
            storage: { name: t('Stored'), type: 'line', get: (d) => d.max_bucket_bytes, fmt: fmtBytes },
            requests: { name: t('Requests'), type: 'bar', get: (d) => (Number(d.class_a) || 0) + (Number(d.class_b) || 0), fmt: fmtCount },
            egress: { name: t('Data transfer'), type: 'bar', get: (d) => (Number(d.egress) || 0) + (Number(d.cdn_bytes) || 0), fmt: fmtBytes },
        }[metric];
        const series = metric === 'requests'
            ? [
                { name: t('Class A'), type: 'bar', stack: 'r', data: daily.map((d) => Number(d.class_a) || 0) },
                { name: t('Class B'), type: 'bar', stack: 'r', data: daily.map((d) => Number(d.class_b) || 0) },
            ]
            : [{ name: M.name, type: M.type, smooth: true, areaStyle: M.type === 'line' ? { opacity: 0.15 } : undefined, data: daily.map((d) => Number(M.get(d)) || 0) }];
        return {
            grid: { left: 8, right: 16, top: 30, bottom: 8, containLabel: true },
            tooltip: {
                trigger: 'axis',
                valueFormatter: (v) => M.fmt(v),
            },
            legend: metric === 'requests' ? { top: 0 } : undefined,
            xAxis: { type: 'category', data: daily.map((d) => String(d.day || '').slice(5)) },
            yAxis: { type: 'value', axisLabel: { formatter: (v) => M.fmt(v) } },
            series,
        };
    }, [daily, metric, t]);

    const lineCols = [
        {
            title: t('Item'), dataIndex: 'kind', render: (k) => ({
                storage: t('Storage (GB-month)'), class_a: t('Class A requests'), class_b: t('Class B requests'),
                egress: t('Data transfer'), public: t('Public delivery'),
            }[k] || k),
        },
        { title: t('Quantity'), dataIndex: 'quantity', width: 140, align: 'right', render: (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 3 }) },
        { title: t('Unit price'), dataIndex: 'unit_price', width: 120, align: 'right', render: (v) => usd(v) },
        { title: t('Amount'), dataIndex: 'amount', width: 120, align: 'right', render: (v) => usd(v) },
    ];

    const bucketCols = [
        { title: t('Bucket'), dataIndex: 'name', render: (v, r) => <Link to={userPath(`/s3/buckets/${r.bucket_id}`)}>{v}</Link> },
        { title: t('Storage (GB-month)'), dataIndex: 'storage_gb_month', align: 'right', render: (v) => (+(Number(v) || 0).toFixed(3)).toString() },
        { title: t('Class A'), dataIndex: 'class_a', align: 'right', render: fmtCount },
        { title: t('Class B'), dataIndex: 'class_b', align: 'right', render: fmtCount },
        { title: t('Data transfer'), key: 'transfer', align: 'right', render: (_, r) => fmtGb((Number(r.egress_gb) || 0) + (Number(r.public_gb) || 0)) },
    ];

    return (
        <div>
            <PageHeader title={t('Object Storage usage')} icon={<BarChartOutlined />}
                extra={<DatePicker picker="month" allowClear={false} value={month} onChange={(v) => v && setMonth(v)}
                    disabledDate={(d) => d.isAfter(dayjs(), 'month')} />} />

            <Spin spinning={loading}>
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={24} md={8}>
                        <Card style={{ height: '100%' }}>
                            <Statistic title={ym === dayjs().format('YYYY-MM') ? t('This month (estimate)') : t('Amount')} value={usd(data?.amount)} />
                            {invoices.length > 0 && (
                                <Space direction="vertical" size={2} style={{ marginTop: 12 }}>
                                    {invoices.map((inv) => (
                                        <Link key={inv.invoice_id} to={userPath(`/billing/invoices/${inv.invoice_id}`)}>
                                            {t('Invoice #{{id}}', { id: inv.invoice_id })} · {usd(inv.amount)}
                                        </Link>
                                    ))}
                                </Space>
                            )}
                        </Card>
                    </Col>
                    <Col xs={24} md={16}>
                        <Card title={t('Free tier')} size="small" style={{ height: '100%' }}>
                            <Row gutter={24}>
                                <Col xs={24} sm={12}>
                                    <FreeBar label={t('Storage')} used={totals.storage_gb_month} free={prices.free_storage_gb} fmt={(v) => `${+(v).toFixed(2)} GB`} />
                                    <FreeBar label={t('Data transfer')} used={(Number(totals.egress_gb) || 0) + (Number(totals.public_gb) || 0)} free={prices.free_egress_gb} fmt={(v) => `${+(v).toFixed(2)} GB`} />
                                </Col>
                                <Col xs={24} sm={12}>
                                    <FreeBar label={t('Class A requests')} used={totals.class_a} free={prices.free_class_a} fmt={fmtCount} />
                                    <FreeBar label={t('Class B requests')} used={(Number(totals.class_b) || 0) + (Number(totals.public_requests) || 0)} free={prices.free_class_b} fmt={fmtCount} />
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                <Card size="small" style={{ marginBottom: 16 }} title={t('Daily')}
                    extra={<Segmented size="small" value={metric} onChange={setMetric} options={[
                        { value: 'storage', label: t('Storage') },
                        { value: 'requests', label: t('Requests') },
                        { value: 'egress', label: t('Data transfer') },
                    ]} />}>
                    {daily.length
                        ? <ReactECharts option={chart} notMerge style={{ height: 260 }} />
                        : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                </Card>

                <Card size="small" style={{ marginBottom: 16 }} title={t('Charges')} styles={{ body: { padding: 0 } }}>
                    <Table rowKey={(r, i) => `${r.kind}-${i}`} size="small" columns={lineCols} dataSource={lines} pagination={false}
                        locale={{ emptyText: t('No charges') }}
                        summary={() => (lines.length ? (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={3}><Text strong>{t('Total')}</Text></Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right"><Text strong>{usd(data?.amount)}</Text></Table.Summary.Cell>
                            </Table.Summary.Row>
                        ) : null)} />
                </Card>

                <Card size="small" title={t('By bucket')} styles={{ body: { padding: 0 } }}>
                    <Table rowKey="bucket_id" size="small" columns={bucketCols} dataSource={buckets} pagination={false} scroll={{ x: 720 }} />
                </Card>
            </Spin>
        </div>
    );
};

export default S3Usage;
